import {useEffect} from 'react';
import {ArrowLeft} from 'lucide-react';
import {Link} from 'react-router-dom';
import {Navbar} from './Navbar';
import {Footer} from './Footer';
import {useLanguage} from '../contexts/LanguageContext';

export function Datenschutz() {
    const {lang} = useLanguage();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Navbar/>
            <main className="min-h-screen bg-[#020817] pt-24 pb-20">
                <div className="max-w-3xl mx-auto px-4 md:px-8">
                    <Link
                        to="/"
                        data-testid="back-to-home-privacy"
                        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-8"
                    >
                        <ArrowLeft className="w-4 h-4"/>
                        {lang === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
                    </Link>

                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                        {lang === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy'}
                    </h1>
                    <div className="w-12 h-1 bg-cyan-500 rounded-full mb-10"/>

                    <div className="space-y-8 text-slate-300 leading-relaxed text-sm">

                        <section>
                            <h2 className="text-lg font-semibold text-white mb-3">1. Datenschutz auf einen Blick</h2>
                            <h3 className="font-semibold text-slate-200 mb-2">Allgemeine Hinweise</h3>
                            <p>
                                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren
                                personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene
                                Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-white mb-3">2. Allgemeine Hinweise und
                                Pflichtinformationen</h2>
                            <h3 className="font-semibold text-slate-200 mb-2">Datenschutz</h3>
                            <p>
                                Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir
                                behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen
                                Datenschutzvorschriften sowie dieser Datenschutzerklärung.
                            </p>
                            <p className="mt-3">
                                Wenn Sie diese Website benutzen, werden verschiedene personenbezogene Daten erhoben.
                                Personenbezogene Daten sind Daten, mit denen Sie persönlich identifiziert werden können.
                                Die vorliegende Datenschutzerklärung erläutert, welche Daten wir erheben und wofür wir
                                sie nutzen.
                            </p>

                            <h3 className="font-semibold text-slate-200 mt-5 mb-2">Hinweis zur verantwortlichen
                                Stelle</h3>
                            <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5 space-y-1">
                                <p>Philip Michalski</p>
                                <p>c/o Impressumservice Dein-Impressum</p>
                                <p>Stettiner Str. 41</p>
                                <p>35410 Hungen</p>
                                <p className="mt-4 text-yellow-400">
                                    <strong>Bitte versenden Sie keine Pakete an diese Adresse.</strong>
                                </p>
                                <p>E-Mail: <a href="mailto:info@hosting-community.de"
                                              className="text-cyan-400 hover:underline">info@hosting-community.de</a>
                                </p>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-white mb-3">3. Datenerfassung auf dieser
                                Website</h2>
                            <h3 className="font-semibold text-slate-200 mb-2">Cookies</h3>
                            <p>
                                Diese Website verwendet ausschließlich technisch notwendige Cookies. Wir speichern
                                lediglich Ihre Sprachpräferenz (<code
                                className="bg-slate-800 px-1 rounded text-cyan-400 font-mono text-xs">hc-lang</code>)
                                lokal in Ihrem Browser (localStorage), um Ihnen bei Ihrem nächsten Besuch die zuletzt
                                gewählte Sprache anzuzeigen. Es werden keine Tracking-Cookies oder Cookies von
                                Drittanbietern eingesetzt.
                            </p>

                            <h3 className="font-semibold text-slate-200 mt-5 mb-2">Server-Log-Dateien</h3>
                            <p>
                                Der Provider dieser Website erhebt und speichert automatisch Informationen in
                                sogenannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies
                                sind:
                            </p>
                            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
                                <li>Browsertyp und Browserversion</li>
                                <li>Verwendetes Betriebssystem</li>
                                <li>Referrer URL</li>
                                <li>Hostname des zugreifenden Rechners</li>
                                <li>Uhrzeit der Serveranfrage</li>
                                <li>IP-Adresse</li>
                            </ul>
                            <p className="mt-3">
                                Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die
                                Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-white mb-3">4. Externe Links</h2>
                            <p>
                                Diese Website enthält Links zu externen Hosting-Anbietern. Für die Datenschutzpraktiken
                                dieser Drittanbieter sind wir nicht verantwortlich. Bitte beachten Sie die jeweiligen
                                Datenschutzerklärungen der verlinkten Websites.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-white mb-3">5. Discord</h2>
                            <p>
                                Wir betreiben einen Discord-Server als Community-Plattform. Wenn Sie unserem
                                Discord-Server beitreten, gelten die{' '}
                                <a href="https://discord.com/privacy" target="_blank" rel="noopener noreferrer"
                                   className="text-cyan-400 hover:underline">
                                    Datenschutzrichtlinien von Discord Inc.
                                </a>
                                . Wir haben keinen Einfluss auf die Datenerhebung durch Discord.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-white mb-3">6. Ihre Rechte</h2>
                            <p>Sie haben jederzeit das Recht:</p>
                            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
                                <li>Auskunft über Ihre bei uns gespeicherten Daten zu erhalten (Art. 15 DSGVO)</li>
                                <li>Die Berichtigung unrichtiger Daten zu verlangen (Art. 16 DSGVO)</li>
                                <li>Die Löschung Ihrer Daten zu verlangen (Art. 17 DSGVO)</li>
                                <li>Die Einschränkung der Verarbeitung zu verlangen (Art. 18 DSGVO)</li>
                                <li>Der Verarbeitung zu widersprechen (Art. 21 DSGVO)</li>
                                <li>Beschwerde bei einer Aufsichtsbehörde einzulegen (Art. 77 DSGVO)</li>
                            </ul>
                        </section>
                    </div>
                </div>
            </main>
            <Footer/>
        </>
    );
}
